import { useEffect, useRef, type RefObject } from "react";

export type GoToFolder = "inbox" | "sent" | "drafts" | "trash";

export interface ShortcutHandlers {
    /** Settings → Keyboard shortcuts on/off. */
    enabled?: boolean;
    onCompose: () => void;
    onArchive: () => void;
    onDelete: () => void;
    onNextMessage: () => void;
    onPrevMessage: () => void;
    /** o / Enter — open the selected item (focus the reading pane). */
    onOpen?: () => void;
    /** u — back to the list. */
    onBack?: () => void;
    /** s — star / flag toggle. */
    onToggleStar?: () => void;
    /** Shift+I */
    onMarkRead?: () => void;
    /** Shift+U */
    onMarkUnread?: () => void;
    onReply?: () => void;
    onReplyAll?: () => void;
    onForward?: () => void;
    /** x — toggle the selected row's checkbox. */
    onToggleSelect?: () => void;
    /** g then i / s / d / t */
    onGoTo?: (folder: GoToFolder) => void;
    /** z — undo the last action. */
    onUndo?: () => void;
    /** ? — keyboard-shortcut help. */
    onShowShortcuts?: () => void;
    searchInputRef?: RefObject<HTMLInputElement> | undefined;
}

function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    const tag = target.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/** Menus, dialogs and listboxes own their keys (typeahead, Enter, letters). */
function isInOverlay(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest('[role="menu"],[role="dialog"],[role="alertdialog"],[role="listbox"],[role="combobox"]'));
}

/** Enter on a focused control already activates it — don't double-handle. */
function isActivatable(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest('button,a[href],[role="button"],[role="checkbox"],[role="switch"],summary'));
}

const G_TIMEOUT_MS = 1500;
const GO_KEYS: Record<string, GoToFolder> = { i: "inbox", s: "sent", d: "drafts", t: "trash" };

/**
 * Gmail keyboard shortcuts, active while focus is not in a text field,
 * editor, menu or dialog. EVERY bare-letter shortcut is skipped when Ctrl,
 * Cmd or Alt is held — Ctrl+C / Ctrl+E / Ctrl+K belong to the browser (the
 * old handler hijacked copy that way). Handlers are read through a ref so
 * the listener binds once instead of on every render.
 *
 * Keys: j/k ↓/↑ next/prev · o/Enter open · u back · e archive · # / Delete
 * trash · s star · Shift+I / Shift+U read / unread · r / a / f reply,
 * reply all, forward · c / n / Shift+C compose · x select · / search ·
 * g then i/s/d/t go to folder · z undo · ? help. Ctrl/⌘+N also composes.
 */
export function useMailShortcuts(handlers: ShortcutHandlers) {
    const ref = useRef(handlers);
    ref.current = handlers;
    const pendingG = useRef(0);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const h = ref.current;
            if (h.enabled === false) return;
            if (isTypingTarget(e.target) || isInOverlay(e.target)) return;

            if (e.ctrlKey || e.metaKey) {
                if (e.key.toLowerCase() === "n" && !e.altKey && !e.shiftKey) {
                    e.preventDefault();
                    h.onCompose();
                }
                return;
            }
            if (e.altKey) return;

            const key = e.key;

            // Second key of a "g" sequence.
            if (pendingG.current && Date.now() - pendingG.current < G_TIMEOUT_MS) {
                pendingG.current = 0;
                const folder = !e.shiftKey ? GO_KEYS[key.toLowerCase()] : undefined;
                if (folder && h.onGoTo) {
                    e.preventDefault();
                    h.onGoTo(folder);
                    return;
                }
            }
            pendingG.current = 0;

            // Keys that are typed with Shift on most layouts.
            switch (key) {
                case "?":
                    e.preventDefault();
                    h.onShowShortcuts?.();
                    return;
                case "/":
                    e.preventDefault();
                    h.searchInputRef?.current?.focus();
                    h.searchInputRef?.current?.select();
                    return;
                case "#":
                    e.preventDefault();
                    h.onDelete();
                    return;
            }
            if (e.shiftKey) {
                switch (key.toLowerCase()) {
                    case "i":
                        e.preventDefault();
                        h.onMarkRead?.();
                        return;
                    case "u":
                        e.preventDefault();
                        h.onMarkUnread?.();
                        return;
                    case "c":
                        e.preventDefault();
                        h.onCompose();
                        return;
                }
                return;
            }

            switch (key.toLowerCase()) {
                case "g":
                    pendingG.current = Date.now();
                    return;
                case "c":
                case "n":
                    e.preventDefault();
                    h.onCompose();
                    return;
                case "e":
                    h.onArchive();
                    return;
                case "delete":
                case "backspace":
                    h.onDelete();
                    return;
                case "j":
                case "arrowdown":
                    e.preventDefault();
                    h.onNextMessage();
                    return;
                case "k":
                case "arrowup":
                    e.preventDefault();
                    h.onPrevMessage();
                    return;
                case "o":
                    e.preventDefault();
                    h.onOpen?.();
                    return;
                case "enter":
                    if (isActivatable(e.target)) return;
                    e.preventDefault();
                    h.onOpen?.();
                    return;
                case "u":
                    e.preventDefault();
                    h.onBack?.();
                    return;
                case "s":
                    e.preventDefault();
                    h.onToggleStar?.();
                    return;
                case "x":
                    e.preventDefault();
                    h.onToggleSelect?.();
                    return;
                case "r":
                    e.preventDefault();
                    h.onReply?.();
                    return;
                case "a":
                    e.preventDefault();
                    h.onReplyAll?.();
                    return;
                case "f":
                    e.preventDefault();
                    h.onForward?.();
                    return;
                case "z":
                    e.preventDefault();
                    h.onUndo?.();
                    return;
            }
        };

        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);
}
